import { useCallback, useEffect, useState } from 'react'
import { Loader2, ShieldCheck, ShieldOff } from 'lucide-react'
import toast from 'react-hot-toast'
import dosPasosService from '@/services/dosPasosService'
import { useDosPasosStore } from '@/store/dosPasosStore'
import { mensajeDeError } from '@/lib/errores'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import FlujoDosPasos from '@/components/seguridad/FlujoDosPasos'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * Seguridad de la cuenta: activar y desactivar la verificación en dos pasos.
 *
 * Al asesor cd-2b la API se la EXIGE para cotizar, levantar y atender consultas (`requerido`), así
 * que desactivarla no lo exime: lo deja sin esas pantallas hasta que la vuelva a activar. Sirve
 * para cambiar de teléfono.
 */
export default function Seguridad() {
  const guardarSesion = useDosPasosStore((s) => s.guardarSesion)
  const borrarSesion = useDosPasosStore((s) => s.borrarSesion)
  const [estado, setEstado] = useState(null)
  const [error, setError] = useState(null)
  const [desactivando, setDesactivando] = useState(false)
  const [codigo, setCodigo] = useState('')
  const [enviando, setEnviando] = useState(false)

  const cargar = useCallback(async () => {
    setError(null)
    try {
      setEstado(await dosPasosService.estado())
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo consultar la verificación en dos pasos'))
    }
  }, [])

  useEffect(() => {
    cargar()
  }, [cargar])

  const desactivar = async (evento) => {
    evento.preventDefault()
    setEnviando(true)
    try {
      await dosPasosService.desactivar(codigo.trim())
      borrarSesion()
      setCodigo('')
      setDesactivando(false)
      toast.success('Verificación en dos pasos desactivada')
      await cargar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'Código inválido'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Seguridad"
        description="Protege tu cuenta con un código de tu teléfono además de la contraseña."
      />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Verificación en dos pasos</CardTitle>
            {estado && (
              <Badge variant={estado.activado ? 'success' : 'warning'}>
                {estado.activado ? 'Activada' : 'Sin activar'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!estado && !error && (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-primary" />
            </div>
          )}

          {estado?.requerido && (
            <Alert variant="info">
              <AlertDescription>
                Tu cuenta de asesor la necesita para cotizar, hacer levantamientos y atender consultas.
              </AlertDescription>
            </Alert>
          )}

          {estado && !estado.activado && (
            <FlujoDosPasos activado={false} alVerificar={guardarSesion} alTerminar={cargar} />
          )}

          {estado?.activado && (
            <>
              <div className="flex items-start gap-3 text-body-sm text-foreground">
                <ShieldCheck size={18} className="mt-0.5 shrink-0 text-success" />
                <p>
                  Activada
                  {estado.enrolledAt && ` desde el ${new Date(estado.enrolledAt).toLocaleDateString('es')}`}. Te
                  pediremos el código de tu app autenticadora la primera vez que haga falta; vale 12 horas
                  en cada pestaña.
                </p>
              </div>

              {!desactivando ? (
                <Button variant="outline" onClick={() => setDesactivando(true)}>
                  <ShieldOff size={16} /> Desactivar
                </Button>
              ) : (
                <form onSubmit={desactivar} className="space-y-3 rounded-md border border-line p-4">
                  {estado.requerido && (
                    <Alert variant="warning">
                      <AlertDescription>
                        Mientras esté desactivada no podrás cotizar, hacer levantamientos ni atender
                        consultas. Si cambias de teléfono, desactívala con un código del teléfono viejo (o
                        uno de respaldo) y vuelve a activarla con el nuevo.
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="codigo-desactivar">Código de tu app o de respaldo</Label>
                    <Input
                      id="codigo-desactivar"
                      value={codigo}
                      onChange={(e) => setCodigo(e.target.value)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={32}
                      autoFocus
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="submit" variant="destructive" disabled={enviando || !codigo.trim()}>
                      {enviando && <Loader2 size={16} className="animate-spin" />}
                      Desactivar
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setDesactivando(false)
                        setCodigo('')
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
